import { CommonModule } from '@angular/common'
import { Component, inject, OnInit, signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'
import { MixPanelService } from '@app/analytics/mixpanel.service'
import { ICON } from '@app/cnst/icons.cnst.js'
import { ExternalApp, ROUTES } from '@app/cnst/nav.cnst'
import { isIOSApp, isNativeApp } from '@app/cnst/userDevice.cnst'
import { decorate, ErrorHandlerType, loader, LoaderType } from '@app/decorators/decorators'
import { LoaderDirective } from '@app/dir/loader/loader.directive'
import { TreeViewModal } from '@app/modals/tree-view/tree-view.modal'
import { ValuePickerModal } from '@app/modals/value-picker/value-picker.modal.js'
import { ValuePickerValue } from '@app/modals/value-picker/value-picker.model.js'
import { BaseModal } from '@app/pages/base.modal'
import { AccountService } from '@app/srv/account.service'
import { AdminService } from '@app/srv/admin.service'
import { remoteConfigOverrideLsKey } from '@app/srv/api.service.js'
import { AppleWatchService } from '@app/srv/appleWatch/appleWatch.service'
import { B1Service } from '@app/srv/b1.service'
import { di } from '@app/srv/di.service'
import { EventService } from '@app/srv/event.service'
import { forceExperimentPrefix } from '@app/srv/experiment.service'
import { FileService } from '@app/srv/file.service'
import { HK_CACHE_FILE, HK_CACHE_FOLDER } from '@app/srv/healthkit/healthkit.service'
import { NavService } from '@app/srv/nav.service'
import { PartnerService } from '@app/srv/partner.service'
import { Priority } from '@app/srv/popup.cnst'
import { PopupController } from '@app/srv/popup.controller'
import { QAService, QASyncDataType, SkipQuizOptions } from '@app/srv/qa.service'
import { SessionService } from '@app/srv/session.service'
import { storageService } from '@app/srv/storage.service.js'
import { dispatch, getState, select2 } from '@app/srv/store.service'
import { TourId } from '@app/srv/tour.cnst'
import { TourService } from '@app/srv/tour.service'
import { Clipboard } from '@capacitor/clipboard'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonItemGroup,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonSelect,
  IonSelectOption,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone'
import {
  _groupBy,
  _jsonParseOrUndefined,
  _objectEntries,
  _range,
  AppError,
  IsoDate,
  localDate,
  pHang,
  StringMap,
} from '@naturalcycles/js-lib'
import {
  BirthControlMethodBreakpointOption,
  Experiment,
  getBucket,
  Goal,
  GoalBreakpointOption,
  HardwareId,
  PersonaCategory,
  PersonaDataTM,
  ProductKey,
  shortQuizGoalOptions,
} from '@naturalcycles/shared'
import { env } from '@src/environments/environment.js'
import { OuraSyncABTestMockService } from '@src/experiments/ab362/srv/ouraSyncABTestMock.service.js'
import { NCInAppPurchase, NCSentry } from '@src/typings/capacitor.js'
import { BehaviorSubject } from 'rxjs'

// TODO: Move it to shared if this concept will continue living
const FEATURE_FLAGS = [Experiment.FEATURE_FLAG_OURA_BG_SYNC]

@Component({
  selector: 'page-hub',
  imports: [
    CommonModule,
    LoaderDirective,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonIcon,
    IonTitle,
    IonContent,
    IonList,
    IonItemGroup,
    IonItemDivider,
    IonItemSliding,
    IonItemOptions,
    IonItemOption,
    IonLabel,
    IonItem,
    IonText,
    IonSelect,
    IonSelectOption,
  ],
  templateUrl: './hub.page.html',
  styleUrl: './hub.page.scss',
})
export class HubPage extends BaseModal implements OnInit {
  className = 'HubPage'

  protected override blockPopups = false

  public qaService = inject(QAService)
  private accountService = inject(AccountService)
  private appleWatchService = inject(AppleWatchService)
  private b1Service = inject(B1Service)
  private eventService = inject(EventService)
  private fileService = inject(FileService)
  private partnerService = inject(PartnerService)
  private popupController = inject(PopupController)
  private sessionService = inject(SessionService)
  private tourService = inject(TourService)
  protected router = inject(Router)
  private navService = inject(NavService)
  private mixpanelService = inject(MixPanelService)
  private ourSyncABTestMockService = inject(OuraSyncABTestMockService)

  public readonly QASyncDataType = QASyncDataType

  public completeDate$ = select2(s => s.account.completeDate)
  public goal$ = select2(s => s.account.goal)
  public hardwareId$ = select2(s => s.account.hwId)
  public accountId$ = select2(s => s.account.id)
  protected accountML$ = select2(s => s.account.ml)
  public externalAccountId$ = select2(s => s.account.externalAccountId)
  public partnerId$ = select2(s => s.partnerAccount?.id)
  public partners$ = select2(s => s.partners)
  public ouraAuthorized$ = select2(s => s.oura?.authorized)
  public assignments$ = select2(s => s.experiment.assignments)
  public currentSubscription$ = select2(s => s.subscriptions.current)
  public dismissedUpsellOffers$ = select2(s => s.retailUpsellOffer.dismissedIds)
  public showBluetoothStatus = toSignal(select2(s => s.qaSettings.showBluetoothStatus))

  public enabledFeatureFlags$ = new BehaviorSubject<Experiment[]>([])

  public personasByCategory = signal<Record<number, PersonaDataTM[] | undefined> | undefined>(
    undefined,
  )

  protected favoritePersonas = signal<Record<string, boolean>>({})
  protected hasFavorites = signal<boolean>(false)

  protected readonly PersonaCategory = PersonaCategory
  protected readonly PERSONA_CATEGORY_TO_ICON_MAP: Record<number, ICON> = {
    [PersonaCategory.PREGNANCY]: ICON.PREGNANT_BABY,
    [PersonaCategory.DEMO]: ICON.DIAMOND,
    [PersonaCategory.SUBSCRIPTION]: ICON.GRAPH_COMPARE,
    [PersonaCategory.DEVICES]: ICON.THERMOMETER,
    [PersonaCategory.OTHER]: ICON.INFO,
  }

  protected readonly ICON = ICON

  public showDangerousFeatures = false
  protected forcedFontSize = localStorage.getItem('forcedFontSize')

  public readonly HardwareId = HardwareId
  public readonly ProductKey = ProductKey
  public readonly Goal = Goal
  public readonly Experiment = Experiment
  public readonly ExternalApp = ExternalApp
  public readonly isIOSApp = isIOSApp
  public readonly ROUTES = ROUTES
  public readonly isProduction = env.prod

  public async ngOnInit(): Promise<void> {
    await this.setPersonas()

    const { account } = getState()

    if (account.partnersEnabled) {
      await this.partnerService.getPartners()
    }

    if (!this.isProduction || account.testUser) {
      this.showDangerousFeatures = true
    }

    this.subscriptions.push(
      this.assignments$.subscribe(assignments => {
        const value = FEATURE_FLAGS.filter(ff => {
          return _objectEntries(assignments)
            .map(([id, assignment]) => {
              if (id && getBucket(assignment) === 'test') return id
            })
            .includes(ff)
        })

        this.enabledFeatureFlags$.next(value)
      }),
    )
  }

  private async setPersonas(): Promise<void> {
    const personas = await this.qaService.getPersonas()
    if (!personas.length) return

    this.personasByCategory.set(_groupBy<PersonaDataTM>(personas, p => p.category))

    // Get favorite personas from LS and set the signal
    const favoritePersonas = storageService.get('favoritePersonas')
    if (favoritePersonas) {
      this.favoritePersonas.set(JSON.parse(favoritePersonas))
      this.hasFavorites.set(Object.values(this.favoritePersonas()).includes(true))
    }
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async addPartner(): Promise<void> {
    await this.qaService.addPartner()
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async loginToPartner(id?: string): Promise<void> {
    id ||= prompt('partner id or email') || undefined

    if (!id) return

    await this.qaService.loginToPartner(id)
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async logout(): Promise<void> {
    await this.sessionService.logout()
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async logoutFromRedDot(): Promise<void> {
    di.get(AdminService).redirectToLogout()
    await pHang()
  }

  public async loginToAccount(accId?: string): Promise<void> {
    const idOrEmail = accId || prompt('accountId or email')
    if (!idOrEmail) return
    await this.qaService.loginToAccount(idOrEmail)
  }

  public async loginToPersona(code: string): Promise<void> {
    await this.qaService.loginToPersona(code, true)
  }

  protected toggleFavorites(persona: PersonaDataTM, slidingItem: IonItemSliding): void {
    // If persona already is in favorites -> remove from favorites, else -> add to favorites
    this.favoritePersonas.update(oldRecord => ({
      ...oldRecord,
      [persona.code]: !oldRecord[persona.code],
    }))
    // Update localstorage with new record
    storageService.putJson('favoritePersonas', this.favoritePersonas())
    this.hasFavorites.set(Object.values(this.favoritePersonas()).includes(true))
    void slidingItem.close()
  }

  protected toggleBluetoothStatus(): void {
    dispatch('extendQASettings', { showBluetoothStatus: !this.showBluetoothStatus() })
  }

  public async startTour(): Promise<void> {
    const { goal, demoMode } = getState().account
    const isPartner = !!getState().partnerAccount

    if (isPartner) {
      void this.tourService.startTour(TourId.PARTNER)
    } else if (goal === Goal.RECOVERY) {
      void this.tourService.startTour(TourId.RECOVERY)
    } else if (goal === Goal.POSTPARTUM) {
      void this.tourService.startTour(TourId.POSTPARTUM)
    } else if (demoMode) {
      void this.tourService.startTour(TourId.DEMO)
    } else {
      void this.tourService.startTour(TourId.APP)
    }

    void this.dismissModal()
  }

  @loader(LoaderType.BUTTON)
  public async showState(): Promise<void> {
    const modal = await this.popupController.presentModalAsAlert(
      {
        component: TreeViewModal,
        componentProps: { data: getState() },
      },
      'modal-treeView',
      Priority.IMMEDIATE,
    )
    await modal.onDidDismiss()
  }

  @loader(LoaderType.BUTTON)
  public async toggleMLFlag(): Promise<void> {
    // TODO [2025-04-31] retire AccountPatchInput.ml
    await this.accountService.patch({ ml: !getState().account.ml })
    await this.dismissModal()
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async createMessage(): Promise<void> {
    const msgKey = prompt('msgkey')
    if (!msgKey) return

    await this.qaService.createMessage(msgKey)

    await this.dismissModal()
  }

  public clearUserSettings(): void {
    dispatch('clearUserSettings')
    dispatch('setLastAutoOpen', undefined)
    void this.dismissModal()
  }

  public clearAddDataState(): void {
    dispatch('resetAddData')
    void this.dismissModal()
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async setTestBucket(): Promise<void> {
    const experimentId = prompt('Experiment ID')?.toUpperCase()
    if (!experimentId) return

    const bucket = prompt('Bucket name')
    if (!bucket) return

    localStorage.setItem(`${forceExperimentPrefix}${experimentId}`, bucket)
    location.reload()
    await pHang()
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async clearTestBuckets(): Promise<void> {
    const { assignments } = getState().experiment

    Object.keys(assignments).forEach(id => {
      localStorage.removeItem(`${forceExperimentPrefix}${id}`)
    })

    this.mixpanelService.reset()

    location.reload()
    await pHang()
  }

  public async skipAppQuiz(isGranular?: boolean): Promise<void> {
    let options: SkipQuizOptions = {
      fertilityGoal: GoalBreakpointOption.prevent,
      birthControlMethod: BirthControlMethodBreakpointOption.condom,
    }
    if (isGranular) {
      const granularOptions = await this.getSkipQuizOptions()
      if (!granularOptions) return
      options = granularOptions
    }
    await this.qaService.bypassQuiz(options)
    void this.dismissModal()
  }

  public async setMockLocation(event: CustomEvent): Promise<void> {
    let countryCode = event.detail.value as string | undefined
    let region: string | null = null

    if (countryCode === 'XXX') {
      countryCode = prompt('Enter country code (2 characters)')!
    } else if (countryCode === 'YYY') {
      localStorage.removeItem('e2eCountry')
      localStorage.removeItem('e2eRegion')
      location.reload()
      await pHang()
    }

    if (countryCode === 'US') {
      region = prompt('Enter region (4 characters)')
    }

    if (!countryCode) return

    localStorage.setItem('e2eCountry', countryCode.toUpperCase())

    if (region) localStorage.setItem('e2eRegion', region)
    else localStorage.removeItem('e2eRegion')
    location.reload()
    await pHang()
  }

  protected async setFontSize(event: CustomEvent): Promise<void> {
    const fontSize = event.detail.value as string | undefined

    if (!fontSize) return

    if (fontSize === 'default') {
      localStorage.removeItem('forcedFontSize')
    } else {
      localStorage.setItem('forcedFontSize', fontSize)
    }

    location.reload()
    await pHang()
  }

  public async addData(): Promise<void> {
    const numberOfCycles = parseInt(prompt('Number of cycles', '1')!) || 1
    if (!numberOfCycles) return
    const cycleDay = parseInt(prompt('Cycle day', '13')!) || 13
    if (!cycleDay) return

    const includeTrackers = window.confirm(
      'Do you want to add trackers as well? Cancel to skip adding trackers.',
    )

    void this.dismissModal()

    await this.qaService.addDataToPersona(
      numberOfCycles,
      cycleDay,
      undefined,
      undefined,
      !includeTrackers,
    )
  }

  public async makePregnant(): Promise<void> {
    const numberOfCycles = parseInt(prompt('Number of cycles', '1')!) || 1

    void this.dismissModal()

    await this.qaService.makePersonaPregnant(numberOfCycles)
  }

  public async addInconsistentData(): Promise<void> {
    const numberOfCycles = parseInt(prompt('Number of cycles', '1')!) || 1
    const cycleDay = parseInt(prompt('Cycle day', '13')!) || 13
    const cycleLength = parseInt(prompt('Cycle length', '32')!) || 32
    const measureRatio =
      parseFloat(prompt('Measure ratio (0.12 means %88 measure)', '0.12')!) || 0.12

    void this.dismissModal()

    await this.qaService.addDataToPersona(numberOfCycles, cycleDay, cycleLength, measureRatio)
  }

  public async makePregnantWithDate(): Promise<void> {
    const numberOfCycles = parseInt(prompt('Number of cycles', '1')!) || 1
    const pregDate = prompt('Date - go back only a few days please!', localDate.todayString())!

    void this.dismissModal()

    await this.qaService.makePersonaPregnant(numberOfCycles, pregDate)
  }

  public async addRealPregnantData(): Promise<void> {
    void this.dismissModal()

    await this.qaService.addRealPregnantData()
  }

  public async changeCompleteDate(): Promise<void> {
    const { account } = getState()
    const completeDate = prompt(
      `Current date is: ${account.completeDate}. What is the new date?`,
    ) as IsoDate | null
    if (!completeDate) {
      alert('Wrong date 📆 The date format is YYYY-MM-DD')
      return
    }

    void this.dismissModal()

    await this.qaService.setCompleteDate(completeDate)
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async changeGoal(event: CustomEvent): Promise<void> {
    const { account } = getState()
    if (account.completeDate) {
      await this.accountService.changeGoal({ goal: event.detail.value })
    } else {
      await this.accountService.patch({ goal: event.detail.value })
    }

    location.reload()
    await pHang()
  }

  public showPerf(): void {
    const prflog = window.prflogString()
    alert(prflog)
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public triggerErrorDialog(): void {
    this.triggerError()
  }

  public triggerError(): void {
    if (isNativeApp) {
      void NCSentry.mockError()
    } else {
      const cause = new Error('hey I have caused the error')

      throw new AppError(
        `Test error`,
        {
          some: 'value',
        },
        {
          cause,
        },
      )
    }
  }

  public async deleteHKCache(): Promise<void> {
    const { id: accId } = getState().account
    await this.fileService.deleteHashFile(HK_CACHE_FOLDER, HK_CACHE_FILE, accId)
    void this.dismissModal()
  }

  public toggleHardwareId(event: CustomEvent): void {
    const id = event.detail.value

    void this.qaService.toggleHardwareId(id)
  }

  public buyThermometer(event: CustomEvent): void {
    const key = event.detail.value as ProductKey

    void this.qaService.buyThermometer(key)
  }

  protected async connectToOuraMockedAccount(): Promise<void> {
    await this.qaService.connectToOuraMockedAccount()
    void this.dismissModal()
  }

  // Used for Oura and B1
  public async syncData(dataType?: QASyncDataType): Promise<void> {
    const nDays = parseInt(prompt('number of days', '1') || '')
    if (isNaN(nDays) || !nDays) return

    const endDate = prompt('End date', localDate.todayString()) as IsoDate | null
    if (!endDate || !localDate.isValidString(endDate)) return

    const { hwId } = getState().account
    if (hwId === HardwareId.B1) this.b1Service.ongoingSyncingState.set(undefined)
    const res = await this.qaService.syncData(nDays, endDate, hwId, dataType)
    if (!res) return

    void this.dismissModal()
  }

  protected async getOuraInsights(): Promise<void> {
    const uf = this.ourSyncABTestMockService.getMockUfForOuraInsights()
    if (!uf) return
    dispatch('setUf', uf)
    void this.dismissModal()
  }

  public async disconnectOuraAccount(): Promise<void> {
    await this.qaService.disconnectOuraAccount()

    void this.dismissModal()
  }

  public async addAppleWatchData(): Promise<void> {
    const dayCount = parseInt(prompt('Number of days', '1') || '')
    if (isNaN(dayCount) || !dayCount) return

    const endDate = prompt('End date (YYYY-MM-DD).', localDate.todayString()) as IsoDate
    if (!endDate) return

    const temp =
      dayCount === 1 ? Number(prompt('Temperature (Leave blank to get a random value)')) : undefined

    // Here we make sure the lastActive is set correctly.
    // We do dispatch lastActive twice as we use pairwise in the AW service.
    dispatch('extendUserSettings', {
      lastActive: localDate(endDate).minus(dayCount, 'day').unix,
    })
    dispatch('extendUserSettings', { lastActive: undefined })

    this.appleWatchService.mockWristTemps = _range(0, dayCount).map(i =>
      this.appleWatchService.mockWristTemperatureSample(i, endDate, temp),
    )

    void this.dismissModal()
    this.eventService.onResume$.next()
  }

  public resetWristTemperatureMock(): void {
    this.appleWatchService.mockWristTemps = undefined
  }

  public async copyToClipboard(text: string): Promise<void> {
    // eslint-disable-next-line id-blacklist
    await Clipboard.write({ string: text })

    void this.popupController.presentSnackbar(
      {
        color: 'secondary',
        duration: 5000,
        message: `Copied to clipboard: ${text}`,
      },
      'hub-copy-to-clipboard-snackbar',
      Priority.IMMEDIATE,
    )
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async changeFeatureFlag(event: CustomEvent): Promise<void> {
    const experimentIds = event.detail.value

    FEATURE_FLAGS.forEach(ff => {
      localStorage.setItem(
        `${forceExperimentPrefix}${ff}`,
        experimentIds.includes(ff) ? 'test' : 'control',
      )
    })

    location.reload()
    await pHang()
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async triggerPayment(): Promise<void> {
    this.qaService.triggerPayment()
    void this.dismissModal()
  }

  protected async checkIfAppIsInstalled(appId: ExternalApp): Promise<void> {
    const isAppInstalled = await this.navService.canOpenExternalApp(appId)
    const appKey = Object.keys(ExternalApp).find(
      key => ExternalApp[key as keyof typeof ExternalApp] === appId,
    )
    alert(`App ${appKey} is ${isAppInstalled ? 'installed' : 'not installed'}`)
  }

  private async getSkipQuizOptions(): Promise<SkipQuizOptions | false> {
    const options: SkipQuizOptions = {}
    options.fertilityGoal = await this.presentValuePickerModal<GoalBreakpointOption>([
      { value: 0, label: 'Choose mode', disabled: true },
      { value: GoalBreakpointOption.prevent, label: 'Prevent' },
      { value: GoalBreakpointOption.plan, label: 'Plan' },
      { value: GoalBreakpointOption.trackPregnancy, label: 'Pregnant' },
      { value: GoalBreakpointOption.postPartum, label: 'Postpartum' },
    ])

    if (!options.fertilityGoal) {
      return false
    }

    if (shortQuizGoalOptions.includes(options.fertilityGoal)) {
      return options
    }

    if (options.fertilityGoal === GoalBreakpointOption.prevent) {
      options.birthControlMethod =
        await this.presentValuePickerModal<BirthControlMethodBreakpointOption>([
          { value: 0, label: 'Choose birth control method', disabled: true },
          { value: BirthControlMethodBreakpointOption.condom, label: 'Non-hormonal (condom)' },
          { value: BirthControlMethodBreakpointOption.pill, label: 'Hormonal (pill)' },
        ])
    }

    options.hwId = await this.presentValuePickerModal<HardwareId>([
      { value: 0, label: 'Choose measuring device', disabled: true },
      { value: HardwareId.UEBE_THERMOMETER, label: 'Thermometer' },
      { value: HardwareId.OURA, label: 'Oura Ring' },
      { value: HardwareId.APPLE_WATCH, label: 'Apple Watch' },
    ])

    if (!options.hwId) {
      return false
    }

    if (options.hwId === HardwareId.UEBE_THERMOMETER) {
      return options
    }

    const ownsHw = await this.presentValuePickerModal([
      { value: 0, label: 'Choose ownsHw', disabled: true },
      { value: 1, label: 'Yes' },
      { value: 2, label: 'No' },
    ])

    if (!ownsHw) {
      return false
    }
    options.ownsHw = ownsHw === 1
    return options
  }

  private async presentValuePickerModal<T extends number>(values: ValuePickerValue[]): Promise<T> {
    const modal = await this.popupController.presentModal(
      {
        component: ValuePickerModal,
        cssClass: 'modal--transparent',
        componentProps: {
          renderAll: true,
          alignRight: true,
          width: '80%',
          values,
        },
      },
      'red-dot-value-picker',
      Priority.IMMEDIATE,
    )

    const { data } = await modal.onDidDismiss()

    return data?.value
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async setSubscriptionBillingDates(): Promise<void> {
    const { subscriptions } = getState()
    const firstBillingDate = prompt(
      'First billing date',
      subscriptions.current?.firstBillingDate,
    ) as IsoDate
    const nextBillingDate = prompt(
      'Next billing date',
      subscriptions.current?.nextBillingDate,
    ) as IsoDate

    if (!firstBillingDate || !nextBillingDate) return

    void this.dismissModal()

    await this.qaService.setSubscriptionBillingDates({
      firstBillingDate,
      nextBillingDate,
    })
  }

  public clearDismissedUpsellOffers(): void {
    dispatch('clearDismissedUpsellOffers')
    void this.dismissModal()
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async finishAllIAPTransactions(): Promise<void> {
    if (!this.showDangerousFeatures) return
    await NCInAppPurchase.finishAllTransactions()
    void this.dismissModal()
  }

  public toggleInspector(): void {
    const { inspectorEnabled } = getState().ui
    if (inspectorEnabled) {
      // The inspector has messed with the DOM, so best to reload
      location.reload()
      return
    }

    dispatch('extendUI', { inspectorEnabled: true })
    void this.dismissModal()
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async setOverrideApiURL(): Promise<void> {
    const currentUrl = getState().overrideApiUrl || env.apiUrl
    const overrideApiURL = prompt(
      'Enter the NCBackend3 url you wish to use. Leave empty to default',
      currentUrl,
    )
    if (currentUrl === overrideApiURL || !overrideApiURL) {
      dispatch('setOverrideApiUrl', '')
    } else {
      dispatch('setOverrideApiUrl', overrideApiURL)
    }
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async overrideRemoteConfig(): Promise<void> {
    const propertyKey = prompt('Property key')
    if (!propertyKey) return

    const { remoteConfig } = getState()
    if (!(propertyKey in remoteConfig)) {
      alert(`Invalid property key`)
      return
    }

    const propertyType = typeof remoteConfig[propertyKey as keyof typeof remoteConfig]

    const propertyValue = prompt(`Property value (${propertyType})`)
    if (!propertyValue) return

    const existing: StringMap =
      _jsonParseOrUndefined(storageService.get(remoteConfigOverrideLsKey)) || {}
    existing[propertyKey] = propertyValue
    storageService.put(remoteConfigOverrideLsKey, JSON.stringify(existing))

    location.reload()
    await pHang()
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async clearRemoteConfigOverride(): Promise<void> {
    storageService.remove(remoteConfigOverrideLsKey)

    location.reload()
    await pHang()
  }
}
